import { useId } from 'react';
import { cn } from '@hrms/ui';

/** The two halves of one ellipse (rx 14, ry 3.4) around the mark's centre. The front half is drawn
 *  over the planet and the back half under it, which is what makes the ring read as an orbit. */
const BACK_ARC = 'M6 20 A14 3.4 0 0 1 34 20';
const FRONT_ARC = 'M6 20 A14 3.4 0 0 0 34 20';

export interface BrandMarkProps {
  /** Rendered width and height in px. */
  size?: number;
  className?: string;
}

/**
 * The PeopleOrbit mark: a planet circled by one tilted orbit with a satellite on it — everything
 * about a person, held in one orbit. Built to stay legible from the auth hero down to favicon size.
 *
 * Colours come from the fixed `--hero-*` tokens, for the same reason `--hero-gradient` is fixed:
 * the product's own mark is not white-label-overridable. The SVG ids are namespaced per instance
 * because the sidebar and the mobile drawer can both render a mark on one page, and a duplicated
 * id makes the second instance's `url(#…)` resolve against the first.
 *
 * `public/favicon.svg` is the same drawing with literal colours (a favicon cannot read CSS
 * variables) — keep the two in step by hand.
 */
export function BrandMark({ size = 32, className }: BrandMarkProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const fillId = `${uid}-fill`;
  const glossId = `${uid}-gloss`;
  const cutId = `${uid}-cut`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0 rounded-[27.5%] text-fg-on-accent', className)}
    >
      <defs>
        <linearGradient id={fillId} x1="4" y1="2" x2="36" y2="38" gradientUnits="userSpaceOnUse">
          <stop offset="0" style={{ stopColor: 'var(--hero-3)' }} />
          <stop offset="0.5" style={{ stopColor: 'var(--hero-2)' }} />
          <stop offset="1" style={{ stopColor: 'var(--hero-1)' }} />
        </linearGradient>
        <linearGradient id={glossId} x1="20" y1="0" x2="20" y2="22" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.3" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
        {/* Cuts a gap into the planet where the front of the orbit crosses it. */}
        <mask id={cutId} maskUnits="userSpaceOnUse" x="0" y="0" width="40" height="40">
          <rect width="40" height="40" fill="white" />
          <path d={FRONT_ARC} fill="none" stroke="black" strokeWidth="3.6" strokeLinecap="round" />
        </mask>
      </defs>

      <rect width="40" height="40" rx="11" fill={`url(#${fillId})`} />
      <rect width="40" height="40" rx="11" fill={`url(#${glossId})`} />

      <g transform="rotate(-24 20 20)" fill="currentColor" stroke="currentColor">
        <path d={BACK_ARC} fill="none" strokeWidth="2" strokeOpacity="0.45" />
        <circle cx="20" cy="20" r="8" stroke="none" mask={`url(#${cutId})`} />
        <path d={FRONT_ARC} fill="none" strokeWidth="2.25" strokeLinecap="round" />
        <circle cx="33.15" cy="18.83" r="2.1" stroke="none" />
      </g>
    </svg>
  );
}

/**
 * One lap of the orbit, starting where the static mark draws its satellite (33.15, 18.83 — 20 degrees
 * behind the right-hand end) and running toward the front, so the first frame of the moving mark
 * is the still one. Two half-ellipses, because one SVG arc cannot close on itself.
 */
const ORBIT_LAP = 'M33.15 18.83 A14 3.4 0 0 1 6.85 21.17 A14 3.4 0 0 1 33.15 18.83';
/** Seconds per lap: under the splash's 1.2s minimum is too fast to read as an orbit, so a lap is
 *  a little longer and a person sees at least half of one. */
const ORBIT_LAP_S = 2.6;

export interface BrandOrbitMarkProps {
  /** Rendered width and height in px. */
  size?: number;
  /** Sends the satellite round the orbit, continuously. Off for reduced motion: then it is still. */
  animated?: boolean;
  className?: string;
}

/**
 * The mark without its tile, drawn in `currentColor` for a coloured ground — the launch splash.
 * Same planet, orbit and satellite as {@link BrandMark}, plus a dotted back of the orbit and two
 * small companions that only appear once it moves.
 *
 * When `animated`, the satellite travels the whole ellipse (SMIL `animateMotion`, which runs on the
 * compositor-free SVG timeline and needs no JS). It must pass behind the planet on the back half
 * and in front of it on the front half, so it is drawn twice on the same motion path: once under
 * the planet, and once over it clipped to the front half (the lower half of the un-rotated ellipse).
 *
 * The native Android splash (native-android/res/drawable/splash_icon.xml) is this drawing standing
 * still, so the hand-off from it is invisible. Keep the two in step by hand.
 */
export function BrandOrbitMark({ size = 120, animated = false, className }: BrandOrbitMarkProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const cutId = `${uid}-cut`;
  const frontId = `${uid}-front`;
  const shadeId = `${uid}-shade`;

  const motion = (begin = '0s') =>
    animated ? (
      <animateMotion dur={`${ORBIT_LAP_S}s`} begin={begin} repeatCount="indefinite" path={ORBIT_LAP} />
    ) : null;

  /** Drawn at the origin and moved by its motion path; the static mark sits it on the path's start. */
  const satellite = (
    <g transform={animated ? undefined : 'translate(33.15 18.83)'}>
      <circle r="2.1" stroke="none" />
      {motion()}
    </g>
  );
  const companions = animated ? (
    <g className="brand-orbit-companions" stroke="none">
      <g>
        <circle r="1.1" fillOpacity="0.85" />
        {motion(`-${(ORBIT_LAP_S / 3).toFixed(2)}s`)}
      </g>
      <g>
        <circle r="0.8" fillOpacity="0.7" />
        {motion(`-${((ORBIT_LAP_S * 2) / 3).toFixed(2)}s`)}
      </g>
    </g>
  ) : null;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      aria-hidden="true"
      focusable="false"
      data-animated={animated || undefined}
      className={cn('shrink-0 overflow-visible', className)}
    >
      <defs>
        <radialGradient id={shadeId} cx="17" cy="16.5" r="11" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="currentColor" stopOpacity="1" />
          <stop offset="0.7" stopColor="currentColor" stopOpacity="0.95" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0.8" />
        </radialGradient>
        <mask id={cutId} maskUnits="userSpaceOnUse" x="0" y="0" width="40" height="40">
          <rect width="40" height="40" fill="white" />
          <path d={FRONT_ARC} fill="none" stroke="black" strokeWidth="3.6" strokeLinecap="round" />
        </mask>
        <clipPath id={frontId} clipPathUnits="userSpaceOnUse">
          <rect x="0" y="20" width="40" height="20" />
        </clipPath>
      </defs>

      <g transform="rotate(-24 20 20)" fill="currentColor" stroke="currentColor">
        <path
          d={BACK_ARC}
          fill="none"
          strokeWidth="1.2"
          strokeOpacity="0.7"
          strokeLinecap="round"
          strokeDasharray="0.01 1.6"
        />
        {companions}
        {satellite}
        <circle cx="20" cy="20" r="8" stroke="none" fill={`url(#${shadeId})`} mask={`url(#${cutId})`} />
        <path d={FRONT_ARC} fill="none" strokeWidth="1.6" strokeOpacity="0.85" strokeLinecap="round" />
        {animated && (
          <g clipPath={`url(#${frontId})`}>
            {companions}
            {satellite}
          </g>
        )}
      </g>
    </svg>
  );
}
